import {
  FileText,
  Link2,
  Target,
  ClipboardCheck,
  LayoutGrid,
  MessageSquare,
  BarChart3,
  Layers,
  Search,
  Mail,
  Headphones,
  GitBranch,
  Workflow,
  Users,
  Globe,
  Activity,
  Gauge,
  Shield,
  type LucideIcon,
} from 'lucide-react';
import type { ToolManifest } from '../api/mcp';

export interface ToolGroupDef {
  label: string;
  icon: LucideIcon;
  tools: { name: string; desc: string }[];
}

/** Icon mapping from manifest label to lucide icon */
const ICON_MAP: Record<string, LucideIcon> = {
  "Projects": Layers,
  "Documents": FileText,
  "Document Links": Link2,
  "Cross-Service Linking": Link2,
  "Work Management": ClipboardCheck,
  "Epics": Target,
  "Issues": ClipboardCheck,
  "Boards": LayoutGrid,
  "AI & Orchestration": MessageSquare,
  "AI Editor": MessageSquare,
  "Assessment": BarChart3,
  "Search": Search,
  "Communications": Mail,
  "Support": Headphones,
  "Workspaces": Layers,
  "Content": FileText,
  "Lineage": GitBranch,
  "Handoffs": Workflow,
  "Customer Intelligence": Users,
  "LinkedIn": Globe,
  "Continuous Improvement": Activity,
  "Telemetry": Gauge,
  "ABM & Enrichment": Users,
  "System Health": Activity,
  "Identity & Access": Shield,
};

/** Convert a live manifest into the ToolGroupDef format used by the UI */
export function manifestToToolGroups(manifest: ToolManifest): ToolGroupDef[] {
  return manifest.groups.map((group) => ({
    label: group.label,
    icon: ICON_MAP[group.label] || Layers,
    tools: group.tools.map((t) => ({
      name: t.name,
      desc: t.description,
    })),
  }));
}

/** Hardcoded fallback — used when the manifest endpoint is unavailable */
export const FALLBACK_TOOL_GROUPS: ToolGroupDef[] = [
  {
    label: 'Projects',
    icon: Layers,
    tools: [
      { name: 'martha_list_projects', desc: 'List all projects' },
    ],
  },
  {
    label: 'Documents',
    icon: FileText,
    tools: [
      { name: 'martha_list_documents', desc: 'List project documents' },
      { name: 'martha_get_document', desc: 'Get a document by ID' },
      { name: 'martha_create_document', desc: 'Create a new document' },
      { name: 'martha_update_document', desc: 'Update an existing document' },
      { name: 'martha_delete_document', desc: 'Delete a document' },
      { name: 'martha_search_documents', desc: 'Search documents by query' },
    ],
  },
  {
    label: 'Document Links',
    icon: Link2,
    tools: [
      { name: 'martha_link_documents', desc: 'Create link between two documents' },
      { name: 'martha_get_document_links', desc: 'Get all links for a document' },
      { name: 'martha_delete_document_link', desc: 'Remove a document link' },
    ],
  },
  {
    label: 'Cross-Service Linking',
    icon: Link2,
    tools: [
      { name: 'martha_link_document_to_issue', desc: 'Bidirectional doc-issue link' },
      { name: 'martha_link_document_to_epic', desc: 'Link document to an epic' },
      { name: 'martha_unlink_document_from_issue', desc: 'Remove doc-issue link' },
      { name: 'martha_unlink_document_from_epic', desc: 'Remove doc-epic link' },
    ],
  },
  {
    label: 'Epics',
    icon: Target,
    tools: [
      { name: 'martha_list_epics', desc: 'List all epics' },
      { name: 'martha_get_epic', desc: 'Get epic by ID' },
      { name: 'martha_create_epic', desc: 'Create a new epic' },
      { name: 'martha_update_epic', desc: 'Update an epic' },
      { name: 'martha_delete_epic', desc: 'Delete an epic' },
    ],
  },
  {
    label: 'Issues',
    icon: ClipboardCheck,
    tools: [
      { name: 'martha_list_issues', desc: 'List issues (filter by board/epic/status)' },
      { name: 'martha_get_issue', desc: 'Get issue by ID' },
      { name: 'martha_create_issue', desc: 'Create issue (requires boardId)' },
      { name: 'martha_update_issue', desc: 'Update an issue' },
      { name: 'martha_delete_issue', desc: 'Delete an issue' },
    ],
  },
  {
    label: 'Boards',
    icon: LayoutGrid,
    tools: [
      { name: 'martha_list_boards', desc: 'List all boards' },
      { name: 'martha_get_board', desc: 'Get board by ID' },
      { name: 'martha_create_board', desc: 'Create a new board' },
      { name: 'martha_get_board_analytics', desc: 'Board statistics and metrics' },
    ],
  },
  {
    label: 'AI Editor',
    icon: MessageSquare,
    tools: [
      { name: 'martha_chat', desc: 'Chat about a document with AI' },
      { name: 'martha_suggest_edits', desc: 'AI edit suggestions for text' },
      { name: 'martha_summarize_document', desc: 'Generate document summary' },
      { name: 'martha_generate_specification', desc: 'Generate spec from source doc' },
    ],
  },
  {
    label: 'Assessment',
    icon: BarChart3,
    tools: [
      { name: 'martha_check_build_readiness', desc: 'Coverage analysis across project' },
    ],
  },
];

/** @deprecated Use useToolManifest() hook + manifestToToolGroups() instead */
export const TOOL_GROUPS = FALLBACK_TOOL_GROUPS;
